export const MAX_CHECKPOINTS_PER_STAGE = 4;

export const emptyCheckpoint = (order = 1) => ({
  localKey: `cp-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  _id: "",
  order,
  title: "",
  km_start: "",
  km_end: "",
  description_points_text: "",
  is_start: order === 1,
  is_finish: false,
  image: "",
  imageFile: null,
  imagePreview: "",
});

export const normalizeCheckpoints = (data) => {
  if (Array.isArray(data)) return data;
  return data?.data || data?.checkpoints || data?.items || [];
};

const getCategoryId = (categoryId) => {
  if (!categoryId) return "";
  if (typeof categoryId === "string") return categoryId;
  return categoryId._id || categoryId.id || "";
};

const pointsToText = (points) => {
  if (!Array.isArray(points)) return "";
  return points.filter(Boolean).join("\n");
};

const textToPoints = (text) => {
  return String(text || "")
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);
};

export const mapCheckpointToForm = (checkpoint, index = 0) => ({
  localKey: checkpoint?._id || checkpoint?.id || `cp-${index}`,
  _id: checkpoint?._id || checkpoint?.id || "",
  order:
    checkpoint?.order === 0 || checkpoint?.order
      ? Number(checkpoint.order)
      : index + 1,
  title: checkpoint?.title || "",
  km_start:
    checkpoint?.km_start === 0 || checkpoint?.km_start
      ? String(checkpoint.km_start)
      : "",
  km_end:
    checkpoint?.km_end === 0 || checkpoint?.km_end
      ? String(checkpoint.km_end)
      : "",
  description_points_text: pointsToText(checkpoint?.description_points),
  is_start: Boolean(checkpoint?.is_start),
  is_finish: Boolean(checkpoint?.is_finish),
  image: checkpoint?.image || "",
  imageFile: null,
  imagePreview: "",
});

/**
 * Group route checkpoints by stage, picking one category set per stage.
 * If a stage has multiple categories, prefer the category with the most CPs
 * (or an explicit preferredCategoryId).
 */
export const groupCheckpointsByStage = (
  checkpoints = [],
  { preferredCategoryId = "" } = {}
) => {
  const byStage = new Map();

  checkpoints.forEach((cp) => {
    const stageId = cp?.stage_id || cp?.stageId;
    const categoryId = getCategoryId(cp?.category_id || cp?.categoryId);
    if (!stageId || !categoryId) return;

    if (!byStage.has(stageId)) {
      byStage.set(stageId, new Map());
    }
    const byCategory = byStage.get(stageId);
    if (!byCategory.has(categoryId)) {
      byCategory.set(categoryId, []);
    }
    byCategory.get(categoryId).push(cp);
  });

  const result = {};
  byStage.forEach((byCategory, stageId) => {
    let selectedCategoryId = preferredCategoryId;
    let selectedList = selectedCategoryId
      ? byCategory.get(selectedCategoryId)
      : null;

    if (!selectedList?.length) {
      let bestCategoryId = "";
      let bestList = [];
      byCategory.forEach((list, categoryId) => {
        if (list.length > bestList.length) {
          bestList = list;
          bestCategoryId = categoryId;
        }
      });
      selectedCategoryId = bestCategoryId;
      selectedList = bestList;
    }

    const sorted = [...(selectedList || [])].sort(
      (a, b) => Number(a.order || 0) - Number(b.order || 0)
    );

    result[stageId] = {
      checkpoint_category_id: selectedCategoryId,
      checkpoints: sorted.map(mapCheckpointToForm),
    };
  });

  return result;
};

export const mergeCheckpointsIntoStages = (stages = [], checkpoints = []) => {
  const grouped = groupCheckpointsByStage(checkpoints);
  return stages.map((stage) => {
    const stageId = stage._id || stage.id;
    const match = stageId ? grouped[stageId] : null;
    if (!match) {
      return {
        ...stage,
        checkpoint_category_id: stage.checkpoint_category_id || "",
        checkpoints: Array.isArray(stage.checkpoints) ? stage.checkpoints : [],
      };
    }
    return {
      ...stage,
      checkpoint_category_id: match.checkpoint_category_id,
      checkpoints: match.checkpoints,
    };
  });
};

export const isBlankCheckpoint = (checkpoint) => {
  if (!checkpoint) return true;
  return !(
    String(checkpoint.title || "").trim() ||
    String(checkpoint.km_start || "").trim() ||
    String(checkpoint.km_end || "").trim() ||
    String(checkpoint.description_points_text || "").trim() ||
    checkpoint.image ||
    checkpoint.imageFile ||
    checkpoint.is_start ||
    checkpoint.is_finish
  );
};

const parseOptionalNumber = (value) => {
  const raw = String(value ?? "").trim();
  if (!raw) return null;
  const num = Number(raw);
  if (Number.isNaN(num) || !Number.isFinite(num)) return undefined;
  return num;
};

export const buildCheckpointInputs = (checkpoints = []) => {
  return checkpoints
    .filter((cp) => !isBlankCheckpoint(cp))
    .map((cp, index) => {
      const item = {
        order: index + 1,
        title: String(cp.title || "").trim(),
        is_start: Boolean(cp.is_start),
        is_finish: Boolean(cp.is_finish),
      };

      const kmStart = parseOptionalNumber(cp.km_start);
      if (kmStart === undefined) {
        /* invalid — omit and let validation catch */
      } else {
        item.km_start = kmStart;
      }

      const kmEnd = parseOptionalNumber(cp.km_end);
      if (kmEnd === undefined) {
        /* invalid — omit */
      } else {
        item.km_end = kmEnd;
      }

      const points = textToPoints(cp.description_points_text);
      if (points.length) {
        item.description_points = points;
      }

      return item;
    });
};

export const validateStageCheckpoints = (stage) => {
  const errors = {};
  const checkpoints = (stage?.checkpoints || []).filter(
    (cp) => !isBlankCheckpoint(cp)
  );

  if (!checkpoints.length) {
    return errors;
  }

  if (!String(stage?.checkpoint_category_id || "").trim()) {
    errors.checkpoint_category_id =
      "Select a category for this stage's checkpoints.";
  }

  if (checkpoints.length > MAX_CHECKPOINTS_PER_STAGE) {
    errors.checkpoints = `Maximum ${MAX_CHECKPOINTS_PER_STAGE} checkpoints per stage.`;
  }

  const cpErrors = {};
  checkpoints.forEach((cp, index) => {
    const originalIndex = (stage.checkpoints || []).indexOf(cp);
    const key = originalIndex >= 0 ? originalIndex : index;
    const itemErrors = {};

    if (!String(cp.title || "").trim()) {
      itemErrors.title = "Title is required.";
    }

    const kmStart = parseOptionalNumber(cp.km_start);
    if (cp.km_start !== "" && kmStart === undefined) {
      itemErrors.km_start = "Enter a valid km start.";
    }

    const kmEnd = parseOptionalNumber(cp.km_end);
    if (cp.km_end !== "" && kmEnd === undefined) {
      itemErrors.km_end = "Enter a valid km end.";
    }

    if (Object.keys(itemErrors).length) {
      cpErrors[key] = itemErrors;
    }
  });

  if (Object.keys(cpErrors).length) {
    errors.checkpointItems = cpErrors;
  }

  return errors;
};

/**
 * After route create/update, attach returned stage _ids onto form stages
 * by matching stage_number (then existing _id).
 */
export const attachStageIdsFromRoute = (formStages = [], routeStages = []) => {
  const byNumber = new Map();
  (routeStages || []).forEach((stage) => {
    const num = Number(stage?.stage_number);
    if (!Number.isNaN(num)) {
      byNumber.set(num, stage);
    }
  });

  return formStages.map((stage) => {
    const num = Number(stage.stage_number);
    const matched =
      (stage._id &&
        routeStages.find((s) => (s._id || s.id) === stage._id)) ||
      byNumber.get(num);

    if (!matched) return stage;
    return {
      ...stage,
      _id: matched._id || matched.id || stage._id,
    };
  });
};

/**
 * Persist drafted checkpoints via PUT bulk replace, then upload pending images.
 * Skips stages with no category or no filled checkpoints.
 * Note: PUT requires a non-empty array, so clearing every checkpoint for a
 * stage+category is not done here — leave at least one, or delete via API later.
 */
export const syncStageCheckpoints = async ({
  routeId,
  stages = [],
  replaceCheckpoints,
  uploadImage,
}) => {
  if (!routeId) return;

  for (const stage of stages) {
    const stageId = stage._id || stage.id;
    const categoryId = String(stage.checkpoint_category_id || "").trim();
    const filled = (stage.checkpoints || []).filter(
      (cp) => !isBlankCheckpoint(cp)
    );

    if (!stageId || !categoryId || !filled.length) continue;

    const created = await replaceCheckpoints({
      routeId,
      payload: {
        stage_id: stageId,
        category_id: categoryId,
        checkpoints: buildCheckpointInputs(filled),
      },
    });

    const createdList = normalizeCheckpoints(created);
    const sortedCreated = [...createdList].sort(
      (a, b) => Number(a.order || 0) - Number(b.order || 0)
    );

    for (let i = 0; i < filled.length; i += 1) {
      const draft = filled[i];
      const saved = sortedCreated[i];
      const checkpointId = saved?._id || saved?.id;
      if (draft.imageFile && checkpointId && uploadImage) {
        await uploadImage({ checkpointId, file: draft.imageFile });
      }
    }
  }
};
